import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors, fonts, spacing } from '../../../constants';
import SectionCard from '../raiseQueryForm/SectionCard';
import EditQueryAutocompleteField from './EditQueryAutocompleteField';
import EditQueryInputField from './EditQueryInputField';

/**
 * EditQueryAssignmentSection Component
 * Assignment & Configuration section for EditQueryModal
 * 
 * @param {Object} query - Query object
 * @param {Object} formData - Form data object
 * @param {Array} users - Available users for assignment
 * @param {Function} handleAssigneeChange - Assignee change handler
 * @param {Function} handleFocus - Focus handler
 * @param {Function} handleBlur - Blur handler
 * @param {Object} focusedFields - Focused fields state
 * @param {Object} errors - Form errors
 * @param {boolean} loadingUsers - Whether users are loading
 * @param {boolean} canManageAssignments - Whether user can manage assignments
 */
const EditQueryAssignmentSection = ({
  query,
  formData,
  users,
  handleAssigneeChange,
  handleFocus,
  handleBlur,
  focusedFields,
  errors,
  loadingUsers,
  canManageAssignments,
}) => {
  return (
    <SectionCard title="Assignment & Configuration">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: spacing.sm,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <EditQueryAutocompleteField
              label="Assignee"
              required={true}
              options={users}
              getOptionLabel={(option) => {
                if (!option) return '';
                const optionDisplayName = option.full_name || option.name || '';
                return `${optionDisplayName}${option.designation ? ` (${option.designation})` : ''}`;
              }}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={users.find((u) => u._id === formData.assigneeId) || null}
              onChange={handleAssigneeChange}
              onFocus={handleFocus('assigneeId')}
              onBlur={handleBlur('assigneeId')}
              disabled={loadingUsers || !canManageAssignments}
              loading={loadingUsers}
              hasError={!!errors.assigneeId}
              isFocused={focusedFields.assigneeId}
              placeholder={loadingUsers ? 'Loading users...' : 'Type to search assignee...'}
              errorMessage={errors.assigneeId}
              helperText={
                errors.assigneeId
                  ? ''
                  : loadingUsers
                  ? 'Fetching assignable users...'
                  : 'Type to search and choose who should own this query'
              }
              title={
                canManageAssignments
                  ? undefined
                  : 'Only the query owner or current assignee with assignment privileges can reassign this query'
              }
              filterOptions={(options, params) => {
                const searchTerm = params.inputValue.toLowerCase();
                const filtered = options.filter((option) =>
                  (option.full_name || option.name || '').toLowerCase().includes(searchTerm) ||
                  (option.designation || '').toLowerCase().includes(searchTerm)
                );
                return filtered;
              }}
            />
            {!errors.assigneeId && !canManageAssignments && (
              <Typography
                variant="caption"
                sx={{
                  color: colors.textSecondary,
                  mt: '4px',
                  fontSize: fonts.fontSizeXs,
                  display: 'block',
                }}
              >
                Assignment can only be changed by the query owner or current assignee with assignment privileges.
              </Typography>
            )}
        </Box>

        <Box>
          <EditQueryInputField
            label="Escalation Response Time (hours)"
            value={`${query?.slaResponseTime || 24} hours`}
            disabled={true}
            isFocused={focusedFields.slaResponseTime}
          />
          <Typography
            variant="caption"
            sx={{
              color: colors.textSecondary,
              mt: '2px',
              fontSize: fonts.fontSizeXs,
              display: 'block',
            }}
          >
            Expected response time in hours. Default: 7 days
          </Typography>
        </Box>
      </Box>
    </SectionCard>
  );
};

export default EditQueryAssignmentSection;
